import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  SiteService,
  ServiceDeliveryPointService,
  UtilityAccountsService,
  OrganizationsService,
  SidenavService,
} from '@services';
import { Context, ContextSelectorService } from 'ngx-global-nav';
import { BehaviorSubject, combineLatest, lastValueFrom, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
  ServiceDeliveryPoint,
  SDPsDataObject,
  ServiceDeliveryPointStatus,
  PageType,
  ErrorState,
  Site,
  UtilityAccount,
} from '@model';

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
})
export class ListComponent implements OnInit {
  public noSDPs = false;
  public spdList: ServiceDeliveryPoint[] = [];
  public sites: Site[] = [];
  public selectedSDPId = '';
  private isInit = true;
  public selectedSiteId: string;
  public errorLoadingSDPs: ErrorState = { message: '', hasError: false };
  private readonly subscriptions: Subscription[] = [];
  public serviceAccounts: UtilityAccount[] = [];
  public TO_BE_CONFIGURED = 'yet to be configured';

  private siteSelected$ = new BehaviorSubject<boolean>(false);
  private loadingSDPs$ = new BehaviorSubject<boolean>(false);

  public showLoader$: Observable<boolean>;

  public get siteSelected(): boolean {
    return this.siteSelected$.getValue();
  }
  public set siteSelected(value: boolean) {
    this.siteSelected$.next(value);
  }

  public get loadingSDPs(): boolean {
    return this.loadingSDPs$.getValue();
  }
  public set loadingSDPs(value: boolean) {
    this.loadingSDPs$.next(value);
  }

  public get sitesL() {
    return this.siteService.sites$;
  }

  public get loadingOrgs$() {
    return this.organizationsService.loadingOrgs$;
  }

  public get loadingSites$() {
    return this.siteService.loadingSites$;
  }

  constructor(
    private readonly router: Router,
    private readonly siteService: SiteService,
    private readonly sidenavService: SidenavService,
    private readonly orgSelectorService: ContextSelectorService,
    private readonly organizationsService: OrganizationsService,
    private readonly SDPService: ServiceDeliveryPointService,
    private readonly utilityAccountsService: UtilityAccountsService,
    private readonly translateService: TranslateService,
  ) {
    this.utilityAccountsService.getServiceAccounts();
    const serviceAccountsSub = this.utilityAccountsService.serviceAccounts$.subscribe(
      (serviceAccounts: UtilityAccount[]) => {
        this.TO_BE_CONFIGURED = this.translateService.instant('utility.account.sdp.service-accounts.not-configured');
        this.serviceAccounts = serviceAccounts;
      },
    );
    const orgsSub = this.orgSelectorService.currentContext$.subscribe(async (orgs: Context[]) => {
      const org = orgs[0];
      this.selectedSiteId = '';
      this.spdList = [];
      this.errorLoadingSDPs.message = '';
      this.errorLoadingSDPs.hasError = false;
      this.siteSelected = false;
      this.selectedSDPId = '';
      this.loadingSDPs = false;
      this.siteService.setSelectedSite(null);
      this.siteService.selectSite(null);
      this.siteService.markSiteResolutionComplete();
      this.siteService.setIsFirst(true);

      if (org && org.id) {
        await this.siteService.getSites(org.id);
        const currentUrl = this.router.url;
        if (
          !this.router.isActive('/', true) &&
          !this.router.isActive('/create', true) &&
          !currentUrl.includes('/view') &&
          !currentUrl.includes('/edit') &&
          !this.isInit
        ) {
          this.router.navigate([`/`], {});
        }
        this.isInit = false;
      }
    });

    const sitesSub = this.siteService.sites$.subscribe((sites: Site[]) => {
      if (sites) {
        this.selectedSDPId = '';
        if (!this.isView()) {
          this.siteSelected = false;
        } else if (this.selectedSiteId) {
          this.siteSelected = true;
        }
        this.sites = sites;
      }
    });

    const sdpsSub = this.SDPService.serviceDeliveryPoints$.subscribe((data: SDPsDataObject) => {
      const shouldSelectFirst = data.selectFirst || false;
      const selectSdpId = data.selectSdpId;

      this.loadingSDPs = data.isLoading || false;

      if (!!data && !data.hasError && !data.isLoading) {
        this.spdList = data.sdps;
        this.noSDPs = data.sdps.length === 0;
        this.errorLoadingSDPs.message = '';
        this.errorLoadingSDPs.hasError = false;
      }
      if (data.hasError) {
        this.errorLoadingSDPs.message = data.errorMessage;
        this.errorLoadingSDPs.hasError = true;
        this.spdList = [];
        this.noSDPs = true;
      }

      if (this.spdList.length > 0 && selectSdpId && this.selectedSiteId) {
        this.selectSpecificSDP(selectSdpId);
      } else if (this.spdList.length > 0 && shouldSelectFirst && this.selectedSiteId) {
        this.selectFirstSDP();
      } else if (this.spdList.length === 0 && shouldSelectFirst) {
        this.router.navigate([`/`], {});
      }
    });

    const sdpIdSub = this.SDPService.selectedServiceDeliveryPointId$.subscribe(async (SDPId: string) => {
      if (SDPId) {
        this.selectedSDPId = SDPId;
      }
    });

    const selectionSiteSub = this.siteService.siteSelection$.subscribe(async (siteId: string) => {
      if (siteId) {
        this.selectedSiteId = siteId;
        this.siteSelected = true;
        this.spdList = [];
        this.errorLoadingSDPs.message = '';
        this.errorLoadingSDPs.hasError = false;

        try {
          await this.loadSdps({ value: siteId }, false);
        } catch (error) {
          console.error('Error changing site:', error);
        }
      }
    });

    this.subscriptions = [orgsSub, sitesSub, sdpsSub, sdpIdSub, selectionSiteSub, serviceAccountsSub];

    this.showLoader$ = combineLatest([
      this.siteService.loadingSites$,
      this.organizationsService.loadingOrgs$,
      this.loadingSDPs$,
      this.siteService.siteResolutionComplete$,
    ]).pipe(
      map(([loadingSites, loadingOrgs, loadingSDPs, siteResolutionComplete]) => {
        const baseLoading = loadingOrgs || loadingSites || loadingSDPs;
        if (this.isView() || this.isEdit()) {
          return baseLoading || !siteResolutionComplete;
        }
        return baseLoading;
      }),
    );
  }

  public async ngOnInit(): Promise<void> {}

  public async loadSdps($event: { value: string }, forceSelectFirstSite: boolean = true): Promise<void> {
    const shouldUpdateList = true;
    const siteId = $event.value;

    this.spdList = [];
    this.errorLoadingSDPs.message = '';
    this.errorLoadingSDPs.hasError = false;
    this.siteService.markSiteResolutionPending();

    try {
      await lastValueFrom(
        this.SDPService.getServiceDeliveryPoints(siteId, shouldUpdateList, forceSelectFirstSite, false),
      );
      this.siteService.setSelectedSite(siteId);
      this.siteSelected = true;
      this.siteService.setIsFirst(forceSelectFirstSite);
      this.siteService.markSiteResolutionComplete();
    } catch (error) {
      console.error(`Error loading SDPs for site ${siteId}:`, error);
      this.errorLoadingSDPs.message = 'Error loading SDPs';
      this.errorLoadingSDPs.hasError = true;
      this.siteService.markSiteResolutionComplete();
    }
  }

  public getServiceDeliveryPointStatus(serviceDeliveryPoint: ServiceDeliveryPoint): ServiceDeliveryPointStatus {
    if (serviceDeliveryPoint.status === ServiceDeliveryPointStatus.ACTIVE) {
      return ServiceDeliveryPointStatus.ACTIVE;
    }
    return ServiceDeliveryPointStatus.INACTIVE;
  }

  public isEdit(): boolean {
    return this.router.url.includes(PageType.EDIT);
  }

  public isCreate(): boolean {
    return this.router.url.includes(PageType.CREATE);
  }

  public isView(): boolean {
    return this.router.url.includes(PageType.VIEW);
  }

  public getSDPActiveUtilityAccount(serviceDeliveryPointId: string): string {
    const activeServiceAccount = this.serviceAccounts.find(
      (serviceAccount: UtilityAccount) =>
        serviceAccount.serviceDeliveryPointId == serviceDeliveryPointId && serviceAccount.status == 'ACTIVE',
    );
    if (activeServiceAccount) {
      return activeServiceAccount.alternateIds && activeServiceAccount.alternateIds.serviceAccountNumber
        ? activeServiceAccount.alternateIds.serviceAccountNumber
        : this.TO_BE_CONFIGURED;
    }
    return '';
  }

  public onRowClicked(id: string): void {
    this.selectedSDPId = id;
    this.sidenavService.closeIfMobile();
    this.router.navigate([`details/${id}/view`], {});
  }

  public selectFirstSDP(): void {
    if (this.spdList && this.spdList.length > 0) {
      const firstSDPId = this.spdList[0].id;
      this.selectedSDPId = firstSDPId;
      this.router.navigate([`details/${firstSDPId}/view`], {});
    }
  }

  public selectSpecificSDP(sdpId: string): void {
    this.selectedSDPId = sdpId;

    const currentUrl = this.router.url;
    if (!currentUrl.includes(`details/${sdpId}/`)) {
      this.router.navigate([`details/${sdpId}/view`], {});
    }

    setTimeout(() => {
      this.scrollToSDP(sdpId);
    }, 100);
  }

  private scrollToSDP(sdpId: string): void {
    const sdpElement = document.querySelector(`[data-sdp-id="${sdpId}"]`) as HTMLElement;
    if (sdpElement) {
      sdpElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  public routeToCreatePage(): void {
    this.sidenavService.closeIfMobile();
    this.router.navigate([`create`], {});
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }
}
